/**
 * Pure types + transforms for the /commands form.
 *
 * Kept in its own non-client module so the [commandId] server page can
 * call `commandApiToFormState()` during SSR. `CommandFormPage.tsx` has
 * `'use client'` at the top, which marked the entire module client-only
 * and made `commandApiToFormState` un-callable from the server (Next.js
 * threw "Attempted to call ... from the server but ... is on the
 * client" in production).
 *
 * Anything in here MUST stay free of React, hooks, or browser-only APIs
 * so both server and client trees can import it.
 */
import type { CommandIconKey, AutomationChannel } from '@/lib/automations/fallback-data'
import type { CommandContextAttachment } from '@/lib/commands/context-attachments'
import { normalizeContextAttachments } from '@/lib/commands/context-attachments'

export type ExecutionMode = 'ai' | 'deterministic'
export type TriggerType = 'manual' | 'schedule' | 'event' | 'chat'
export type ScheduleCadence = 'preset' | 'cron' | 'interval'
export type DeliveryTarget = 'chat' | 'chat_email'
export type PromptValidationHealth = 'unknown' | 'ok' | 'warning' | 'error'
export type PromptValidationIssueAction = {
  type: 'add_tools'
  tools: string[]
}
export type PromptValidationIssue = {
  kind: string
  message: string
  excerpt: string
  suggestion: string
  line_hint?: number
  action?: PromptValidationIssueAction
}
export type PromptValidationResult = {
  health: Exclude<PromptValidationHealth, 'unknown'>
  errors: PromptValidationIssue[]
  warnings: PromptValidationIssue[]
  latency_ms: number
  cost_cents: number
}

export type CommandFormState = {
  id?: string
  name: string
  description: string
  prompt: string
  icon_key: CommandIconKey
  channel: AutomationChannel
  execution_mode: ExecutionMode
  model_key: string | null
  allowed_tools: string[]
  chat_invocable: boolean
  chat_invocation_hint: string
  deterministic_graph: Record<string, unknown> | null
  trigger_type: TriggerType
  trigger_config: Record<string, unknown>
  delivery_target: DeliveryTarget
  email_recipients: string[]
  email_subject: string
  prompt_health: PromptValidationHealth
  last_validation_result: PromptValidationResult | null
  last_validated_at: string | null
  alert_on_failure: boolean
  alert_recipients: string[]
  lifetime_cost_cents: number
  cost_cents_last_30d: number
  max_runs_per_hour: number
  max_runs_per_day: number
  enabled: boolean
  context_attachments: CommandContextAttachment[]
}

export const blankCommandForm: CommandFormState = {
  name: '',
  description: '',
  prompt: '',
  icon_key: 'sparkles',
  channel: 'workspace',
  execution_mode: 'ai',
  model_key: 'auto',
  allowed_tools: [],
  chat_invocable: false,
  chat_invocation_hint: '',
  deterministic_graph: null,
  trigger_type: 'manual',
  trigger_config: {},
  delivery_target: 'chat',
  email_recipients: [],
  email_subject: '',
  prompt_health: 'unknown',
  last_validation_result: null,
  last_validated_at: null,
  alert_on_failure: false,
  alert_recipients: [],
  lifetime_cost_cents: 0,
  cost_cents_last_30d: 0,
  max_runs_per_hour: 60,
  max_runs_per_day: 500,
  enabled: true,
  context_attachments: [],
}

/**
 * Valid values for `trigger_config.cadence` when `trigger_type === 'schedule'`.
 * Kept in sync with `SCHEDULE_CADENCES` in `lib/commands/validation.ts`.
 */
export const SCHEDULE_CADENCES: readonly ScheduleCadence[] = ['cron', 'interval', 'preset']

/**
 * Default first event type for the trigger dropdown. Kept here so both the
 * form and the normalizer agree without pulling the full event registry.
 * The dropdown itself sorts wired events first — this is the fallback used
 * when a proposal patch changes `trigger_type` to 'event' without providing
 * a `trigger_config.event_type`.
 */
export const DEFAULT_EVENT_TYPE = 'campaign.launched'

/**
 * Ensure `trigger_config` is coherent with `trigger_type` after a state patch.
 *
 * The trigger-type <Select> in `CommandFormPage.tsx` already seeds a valid
 * `trigger_config` in its `onValueChange`, but proposal-apply patches from
 * CommandCopilot and partial edits can leave `trigger_type` and
 * `trigger_config` out of sync — e.g. `{ trigger_type: 'schedule',
 * trigger_config: {} }` — which then fails server validation with
 * "trigger_config.cadence must be cron, interval, or preset".
 *
 * This function is idempotent: if the config already matches the type it is
 * returned as-is (possibly cloned). Otherwise the safe default the dropdown
 * would have seeded is used.
 */
export function normalizeTriggerConfig(
  triggerType: TriggerType,
  triggerConfig: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const config = (triggerConfig && typeof triggerConfig === 'object' && !Array.isArray(triggerConfig))
    ? { ...(triggerConfig as Record<string, unknown>) }
    : {}
  if (triggerType === 'schedule') {
    const cadence = typeof config.cadence === 'string' ? config.cadence : ''
    if (!SCHEDULE_CADENCES.includes(cadence as ScheduleCadence)) {
      return { cadence: 'preset', preset: 'weekday_morning' }
    }
    return config
  }
  if (triggerType === 'event') {
    if (typeof config.event_type !== 'string' || !config.event_type) {
      return { event_type: DEFAULT_EVENT_TYPE }
    }
    return config
  }
  // manual / chat: no per-trigger fields required
  return config
}

/**
 * Transforms a Supabase `commands` row into the in-memory form state the
 * UI uses. Safe to call from a Server Component (does not touch React,
 * hooks, or any client-only API).
 */
export function commandApiToFormState(row: Record<string, unknown>): CommandFormState {
  return {
    id: String(row.id),
    name: String(row.name || ''),
    description: String(row.description || ''),
    prompt: String(row.prompt || ''),
    icon_key: (row.icon_key as CommandIconKey) || 'sparkles',
    channel: (row.channel as AutomationChannel) || 'workspace',
    execution_mode: (row.execution_mode as ExecutionMode) || 'ai',
    model_key: row.model_key ? String(row.model_key) : 'auto',
    allowed_tools: Array.isArray(row.allowed_tools) ? row.allowed_tools.map(String) : [],
    chat_invocable: row.chat_invocable === true,
    chat_invocation_hint: String(row.chat_invocation_hint || ''),
    deterministic_graph: (row.deterministic_graph as Record<string, unknown> | null) || null,
    trigger_type: (row.trigger_type as TriggerType) || 'manual',
    trigger_config: (row.trigger_config as Record<string, unknown>) || {},
    delivery_target: row.delivery_target === 'chat_email' ? 'chat_email' : 'chat',
    email_recipients: Array.isArray(row.email_recipients) ? row.email_recipients.map(String) : [],
    email_subject: String(row.email_subject || ''),
    prompt_health: row.prompt_health === 'ok' || row.prompt_health === 'warning' || row.prompt_health === 'error'
      ? row.prompt_health
      : 'unknown',
    last_validation_result: row.last_validation_result && typeof row.last_validation_result === 'object' && !Array.isArray(row.last_validation_result)
      ? row.last_validation_result as PromptValidationResult
      : null,
    last_validated_at: row.last_validated_at ? String(row.last_validated_at) : null,
    alert_on_failure: row.alert_on_failure === true,
    alert_recipients: Array.isArray(row.alert_recipients) ? row.alert_recipients.map(String) : [],
    lifetime_cost_cents: Number(row.lifetime_cost_cents ?? 0),
    cost_cents_last_30d: Number(row.cost_cents_last_30d ?? 0),
    max_runs_per_hour: Number(row.max_runs_per_hour ?? 60),
    max_runs_per_day: Number(row.max_runs_per_day ?? 500),
    enabled: row.enabled !== false,
    context_attachments: normalizeContextAttachments(row.context_attachments),
  }
}
